/**
 * Owned Media (OML) — reglas de negocio PURAS (sin I/O), compartidas por el
 * backend (validación al guardar, cron) y el front (feedback inmediato en el
 * formulario). Cada regla cita la sección del doc de requerimientos que la
 * origina. Test: `npm run test:oml` (CI).
 *
 * Lo que vive acá:
 *  - cálculos: uplift e incrementales (§5.2 Journey/Campaña, AC-8/9);
 *  - detector de secretos y sanitizado de URL (§5.2 Plataforma y acceso, §13.3, AC-10/11);
 *  - completitud de los 7 bloques mínimos y estado derivado de la ficha (§6.2, §6.4, §12.2);
 *  - máquinas de estado y quién puede disparar cada cambio (§13.2);
 *  - validaciones por entidad ("no se puede X sin Y", §13.3);
 *  - máscara de campos restringidos (§9.3).
 */
import { addBusinessDaysISO, businessDaysBetweenISO, daysBetweenISO } from './dates';
import { normalizeEmail, sameEmail } from './identity';
import { foldKey } from './text-normalize';
import {
  ACCESS_TYPE, ACCOUNT_STATUS, CONTENT_PARTNER_ROLES, DESIGN_ROLES, EXPERIMENT_STATUS, JOURNEY_STATUS, LINK_TYPE_CALENDAR,
  MEASUREMENT_STATUS, OM_ROLE, OML_COMPLETENESS_BLOCKS, OML_JUSTIFY_FIELDS, OML_LISTS, OML_PERMISSIONS, OML_SCHEMA, OML_TRANSITIONS,
  PERSON_TYPE, PROJECT_STATUS, RETENTION_ANALYST_ROLES, RISKY_ACCESS_TYPES, STAKEHOLDER_TYPE,
  type OmlAction, type OmlActorRole, type OmlCompleteness, type OmlCompletenessBlock, type OmlEntityType, type OmlFichaStatus, type OmlFieldDef,
  type OmlListId, type OmlRestrictedGroup,
} from './types/oml';

export type Values = Record<string, unknown>;

export interface OmlIssue {
  field: string;
  /** Código estable (el front lo traduce; el mensaje en español es el fallback). */
  code: string;
  message: string;
  /** Datos del problema (qué registro está en conflicto, qué parámetros se quitaron…). */
  detail?: Record<string, unknown>;
}

export interface OmlValidation {
  errors: OmlIssue[];
  warnings: OmlIssue[];
  /** Campos que el sistema escribe a partir de los cargados. */
  computed: Values;
}

const isEmpty = (v: unknown): boolean =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const arr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

// ─── Cálculos (§5.2 Journey / Campaña recurrente, AC-8, AC-9) ────────────────

export const roundTo = (n: number, decimals = 2): number => {
  const f = 10 ** decimals;
  return Math.round((n + Number.EPSILON) * f) / f;
};

/**
 * Uplift = (CVR test − CVR control) ÷ CVR control, en puntos porcentuales sobre
 * 100. Precisión completa: el redondeo a dos decimales es solo para mostrar.
 * Sin control (0 o vacío) no hay uplift.
 */
export function computeUplift(cvrTest: unknown, cvrControl: unknown): number | null {
  if (!isNum(cvrTest) || !isNum(cvrControl) || cvrControl === 0) return null;
  return ((cvrTest - cvrControl) / cvrControl) * 100;
}

/**
 * Conversiones incrementales = N × (CVR test − CVR control), con los valores de
 * precisión completa y TRUNCADO a entero (92,58 → 92; AC-8). Si falta N, queda
 * vacío y la ficha lo señala (AC-9).
 */
export function computeIncremental(n: unknown, cvrTest: unknown, cvrControl: unknown): number | null {
  if (!isNum(n) || !isNum(cvrTest) || !isNum(cvrControl)) return null;
  // toFixed(9) absorbe el ruido binario (0.1 + 0.2) antes de truncar.
  const exact = Number(((n * (cvrTest - cvrControl)) / 100).toFixed(9));
  return Math.trunc(exact);
}

/** Formato de porcentaje con dos decimales y coma (39,60 %), como pide el doc. */
export function formatPercent(n: number | null | undefined, opts: { sign?: boolean } = {}): string {
  if (!isNum(n)) return '—';
  const v = roundTo(n, 2).toFixed(2).replace('.', ',');
  const sign = opts.sign && n > 0 ? '+' : '';
  return `${sign}${v} %`;
}

// ─── Secretos y URLs (§5.2 Plataforma y acceso, §13.3, AC-10, AC-11) ─────────

const SECRET_LITERAL = /(password|contraseña|contrasena|clave|pass|pwd)\s*:\s*\S/i;
/** Nombres de gestores de contraseñas: nunca son una clave aunque combinen mayúsculas, minúsculas y dígitos. */
const KNOWN_PASSWORD_MANAGERS = new Set(['1password', '1pass', 'lastpass', 'bitwarden', 'keepass', 'keepassxc', 'dashlane', 'nordpass', 'keeper', 'vaultwarden', 'passbolt', 'secretsmanager', 'okta', 'onelogin']);
const HARD_SYMBOL = /[!@#$%^&*()+=[\]{}|\\;'",<>?~`]/;

export interface SecretCheck {
  suspicious: boolean;
  reason?: 'literal' | 'pattern';
  token?: string;
}

/**
 * Advierte si el texto parece contener una clave (§13.3): "password:", "clave:",
 * "pass:", "pwd:" o "contraseña:" seguidos de algo, o una subcadena de 8+
 * caracteres sin espacios que combine mayúsculas, minúsculas y dígitos o
 * símbolos. Los nombres de gestores ("1Password") y las palabras sueltas sin
 * dos puntos ("gestor de contraseñas") no se marcan (AC-10).
 */
export function looksLikeSecret(text: unknown): SecretCheck {
  const s = str(text);
  if (!s) return { suspicious: false };
  const lit = s.match(SECRET_LITERAL);
  if (lit) return { suspicious: true, reason: 'literal', token: lit[0] };
  for (const tok of s.split(/\s+/)) {
    if (tok.length < 8) continue;
    if (/^https?:\/\//i.test(tok) || /^[\w.-]+@[\w.-]+$/.test(tok)) continue;
    if (KNOWN_PASSWORD_MANAGERS.has(tok.toLowerCase().replace(/[^a-z0-9]/g, ''))) continue;
    const hasLower = /[a-záéíóúñ]/.test(tok);
    const hasUpper = /[A-ZÁÉÍÓÚÑ]/.test(tok);
    const hasDigit = /\d/.test(tok);
    const hasSymbol = HARD_SYMBOL.test(tok);
    if (hasLower && hasUpper && (hasDigit || hasSymbol)) return { suspicious: true, reason: 'pattern', token: tok };
  }
  return { suspicious: false };
}

export interface SanitizedUrl {
  url: string;
  removed: string[];
  valid: boolean;
}

/**
 * Quita de la URL únicamente los parámetros de la lista (editable desde el
 * panel) y conserva los funcionales (gid, node-id, sourcedoc, slide, id, page).
 * También revisa el fragmento (#gid=… de Sheets, ?rlkey=… de Dropbox). Devuelve
 * qué quitó para avisarlo (AC-11).
 */
export function sanitizeUrl(raw: unknown, paramsToStrip: readonly string[]): SanitizedUrl {
  const input = str(raw).trim();
  if (!input) return { url: '', removed: [], valid: true };
  const strip = new Set(paramsToStrip.map((p) => p.toLowerCase()));
  let u: URL;
  try {
    u = new URL(input);
  } catch {
    return { url: input, removed: [], valid: false };
  }
  const removed: string[] = [];
  for (const key of [...u.searchParams.keys()]) {
    if (strip.has(key.toLowerCase())) {
      u.searchParams.delete(key);
      removed.push(key);
    }
  }
  if (u.hash && /[?&=]/.test(u.hash)) {
    const hash = u.hash.replace(/^#/, '');
    const [head, ...rest] = hash.includes('?') ? hash.split('?') : ['', hash];
    const query = rest.join('?');
    const kept: string[] = [];
    for (const pair of query.split('&')) {
      if (!pair) continue;
      const k = pair.split('=')[0];
      if (strip.has(k.toLowerCase())) removed.push(k);
      else kept.push(pair);
    }
    const rebuilt = hash.includes('?') ? `${head}${kept.length ? `?${kept.join('&')}` : ''}` : kept.join('&');
    u.hash = rebuilt ? `#${rebuilt}` : '';
  }
  return { url: u.toString(), removed: [...new Set(removed)], valid: true };
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const DATE_MONTH_RE = /^\d{4}-\d{2}(-\d{2})?$/;

/** 'YYYY-MM' o 'YYYY-MM-DD' → fecha comparable ('YYYY-MM-01' si solo hay mes). */
export function dateMonthToISO(v: unknown): string | null {
  const s = str(v).trim();
  if (!DATE_MONTH_RE.test(s)) return null;
  return s.length === 7 ? `${s}-01` : s;
}

/** Mes y año en español para las fechas con precisión de mes ("febrero 2026"). */
export function formatDateMonth(v: unknown): string {
  const s = str(v).trim();
  if (!DATE_MONTH_RE.test(s)) return s;
  const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const [y, m, d] = s.split('-');
  const mes = MESES[Number(m) - 1] ?? m;
  return d ? `${Number(d)} de ${mes} de ${y}` : `${mes} ${y}`;
}

// ─── Asignaciones y equipo de la cuenta (§8.3 "equipo de la cuenta", §9.3) ───

export interface AssignmentLike {
  personType?: unknown;
  email?: unknown;
  role?: unknown;
  from?: unknown;
  to?: unknown;
  archived?: unknown;
  dedication?: unknown;
  isPrimary?: unknown;
}

/** Vigente = Desde ya cumplida y Hasta vacía o futura. */
export function isAssignmentActive(a: AssignmentLike, today: string): boolean {
  if (a.archived) return false;
  const from = str(a.from);
  const to = str(a.to);
  if (from && from > today) return false;
  if (to && to < today) return false;
  return true;
}

/** Miembro del equipo de la cuenta = asignación vigente de una persona de Winclap. NO incluye freelos. */
export function isTeamMember(a: AssignmentLike, today: string): boolean {
  return isAssignmentActive(a, today) && str(a.personType) === PERSON_TYPE.WINCLAP && !!str(a.email);
}

export function growthPartnerEmails(assignments: AssignmentLike[], today: string): string[] {
  return [...new Set(assignments.filter((a) => isTeamMember(a, today) && str(a.role) === OM_ROLE.GROWTH_PARTNER).map((a) => normalizeEmail(str(a.email))))];
}

/** Rol de asignación → rol de actor del apartado. */
export function actorRoleOfAssignment(role: unknown): OmlActorRole | null {
  const r = str(role);
  if (r === OM_ROLE.GROWTH_PARTNER) return 'gp';
  if (CONTENT_PARTNER_ROLES.includes(r)) return 'cp';
  if (RETENTION_ANALYST_ROLES.includes(r)) return 'ra';
  if (r === OM_ROLE.LEAD_OMC || r === OM_ROLE.RETENTION_LEAD) return 'lead';
  return null;
}

/** Roles que una persona tiene en una cuenta según sus asignaciones vigentes. */
export function rolesInAccount(email: string, assignments: AssignmentLike[], today: string): OmlActorRole[] {
  const roles = new Set<OmlActorRole>();
  for (const a of assignments) {
    if (!isTeamMember(a, today) || !sameEmail(str(a.email), email)) continue;
    const r = actorRoleOfAssignment(a.role);
    if (r) roles.add(r);
  }
  return [...roles];
}

// ─── Estado de la ficha y completitud (§6.2, §6.4, §12.2, AC-24, AC-34) ──────

const NO_APLICA_STATUSES: readonly string[] = [ACCOUNT_STATUS.PAUSED, ACCOUNT_STATUS.CLOSED, ACCOUNT_STATUS.ARCHIVED];

/** "Al día" / "Desactualizada" solo para Activas y En onboarding; el resto "No aplica". DERIVADO, nunca cargado. */
export function fichaStatus(account: { status?: unknown; lastReviewedAt?: unknown }, today: string, staleDays = 30): OmlFichaStatus {
  if (NO_APLICA_STATUSES.includes(str(account.status))) return 'no_aplica';
  const last = str(account.lastReviewedAt).slice(0, 10);
  if (!last) return 'desactualizada';
  return daysBetweenISO(last, today) >= staleDays ? 'desactualizada' : 'al_dia';
}

export function daysSince(iso: unknown, today: string): number | null {
  const d = str(iso).slice(0, 10);
  return d ? daysBetweenISO(d, today) : null;
}

export interface AccountChildren {
  stakeholders: Values[];
  platforms: Values[];
  assignments: AssignmentLike[];
  channelRules: Values[];
  links: Values[];
}

const live = (x: Values) => !x.archived;

/** Requeridos "de verdad" de la información general (los que bloquean el guardado). */
const GENERAL_FIELDS = ['name', 'client', 'countries', 'serviceTypes', 'startDate', 'status', 'functionalOwner', 'clientSummary', 'mainObjective', 'metricsProvider'];

/**
 * Los siete bloques mínimos (§6.4 = §12.2): información general, estado actual
 * y pendientes, ≥1 stakeholder del cliente, ≥1 plataforma con tipo de acceso,
 * ≥1 persona asignada, reglas de canal y ≥1 link al calendario. El indicador
 * mide exactamente esos siete; los recomendados que faltan se listan aparte.
 */
export function accountCompleteness(account: Values, children: AccountChildren, today: string): OmlCompleteness {
  const ok: Record<OmlCompletenessBlock, boolean> = {
    general: GENERAL_FIELDS.every((k) => !isEmpty(account[k])),
    currentStatus: !isEmpty(account.currentStatus),
    stakeholder: children.stakeholders.some((s) => live(s) && str(s.stakeholderType) === STAKEHOLDER_TYPE.CLIENT && s.active !== false),
    platform: children.platforms.some((p) => live(p) && !p.revokedAt && !isEmpty(p.accessType)),
    assignment: children.assignments.some((a) => isTeamMember(a, today)),
    channelRule: children.channelRules.some(live),
    calendarLink: children.links.some((l) => live(l) && str(l.linkType) === LINK_TYPE_CALENDAR),
  };
  const blocks = OML_COMPLETENESS_BLOCKS.map((key) => ({ key, ok: ok[key] }));
  const done = blocks.filter((b) => b.ok).length;
  const defs = OML_SCHEMA.account.fields;
  const missingRecommended = defs.filter((f) => f.recommended && isEmpty(account[f.key])).map((f) => f.key);
  const missingRequired = defs.filter((f) => f.required && !f.system && isEmpty(account[f.key])).map((f) => f.key);
  return { percent: Math.round((done / blocks.length) * 100), blocks, missingRecommended, missingRequired };
}

// ─── Máquinas de estado (§13.2) ──────────────────────────────────────────────

export function statusFieldOf(type: OmlEntityType): string | null {
  return OML_SCHEMA[type].fields.find((f) => f.isStatus)?.key ?? null;
}

export interface TransitionCheck {
  ok: boolean;
  code?: 'no_transition' | 'role';
  message?: string;
  allowedRoles?: OmlActorRole[];
}

/** Lead OMC y Admin del apartado pueden todo (§9.2 "Editar todo"). `system` es el cron/el guardado automático. */
const SUPER: readonly OmlActorRole[] = ['lead', 'admin', 'system'];

export function canTransition(type: OmlEntityType, from: string, to: string, actorRoles: readonly OmlActorRole[]): TransitionCheck {
  if (from === to) return { ok: true };
  const table = OML_TRANSITIONS[type];
  if (!table) return { ok: true };
  const options = table[from] ?? [];
  const t = options.find((o) => o.to === to);
  if (!t) {
    return { ok: false, code: 'no_transition', message: `No se puede pasar de "${from}" a "${to}". Desde "${from}" se puede ir a: ${options.map((o) => `"${o.to}"`).join(', ') || 'ningún estado'}.` };
  }
  if (actorRoles.some((r) => SUPER.includes(r)) || t.by.some((r) => actorRoles.includes(r))) return { ok: true };
  return { ok: false, code: 'role', allowedRoles: t.by, message: `Solo ${describeRoles([...t.by, 'lead'])} puede pasar a "${to}".` };
}

const ROLE_LABEL: Record<OmlActorRole, string> = {
  admin: 'el Admin del apartado', lead: 'el Lead OMC', direction: 'Dirección', gp: 'el Growth Partner', cp: 'el Content Partner', ra: 'el Retention Analyst', nominal: 'un acceso nominal', system: 'el sistema',
};
export function describeRoles(roles: readonly OmlActorRole[]): string {
  const names = [...new Set(roles)].filter((r) => r !== 'system').map((r) => ROLE_LABEL[r]);
  return names.length <= 1 ? names[0] ?? 'el Lead OMC' : `${names.slice(0, -1).join(', ')} o ${names[names.length - 1]}`;
}

// ─── Permisos por entidad (§8.3 ∩ §9.2) ──────────────────────────────────────

/** Permiso efectivo sobre una entidad: intersección de la matriz 9.2 (rol general) y la tabla 8.3 (por entidad). Manda 8.3. */
export function canDoOnEntity(type: OmlEntityType, action: OmlAction, actorRoles: readonly OmlActorRole[]): boolean {
  if (actorRoles.includes('admin') || actorRoles.includes('lead')) {
    // Lead/Admin pueden todo salvo eliminar donde la entidad no lo admite (§8.3 Eliminar = No).
    if (action === 'delete') return OML_SCHEMA[type].actions.delete;
    if (action === 'duplicate') return OML_SCHEMA[type].actions.duplicate;
    if (action === 'archive') return OML_SCHEMA[type].actions.archive;
    return true;
  }
  const allowed = OML_PERMISSIONS[type][action];
  if (!allowed.length) return false;
  if (action === 'duplicate' && !OML_SCHEMA[type].actions.duplicate) return false;
  if (action === 'archive' && !OML_SCHEMA[type].actions.archive) return false;
  return actorRoles.some((r) => allowed.includes(r));
}

/** Procesos que solo edita el Lead OMC aunque el CP/RA edite el resto (§8.3 Proceso operativo). */
export function processTypeIsLeadOnly(processType: unknown, leadOnly: readonly string[]): boolean {
  return leadOnly.includes(str(processType));
}

// ─── Justificación (§8.5, §13.3) ─────────────────────────────────────────────

export function fieldsChanged(prev: Values | null, next: Values, keys: readonly string[]): string[] {
  return keys.filter((k) => !sameValue(prev?.[k], next[k]));
}

export function sameValue(a: unknown, b: unknown): boolean {
  if (isEmpty(a) && isEmpty(b)) return true;
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
  if (typeof a === 'object' || typeof b === 'object') return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  return a === b;
}

/**
 * Justificación escrita obligatoria en tres casos: cambiar Deal actual, Topes o
 * Capacity (Dedicación incluida); dar de alta un acceso de cuenta compartida o
 * personal; cerrar una cuenta.
 */
export function justificationRequired(type: OmlEntityType, prev: Values | null, next: Values): { required: boolean; because: string[] } {
  const because: string[] = [];
  const justify = OML_JUSTIFY_FIELDS[type] ?? [];
  for (const k of fieldsChanged(prev, next, justify)) {
    if (type === 'platform' && k === 'accessType') {
      if (RISKY_ACCESS_TYPES.includes(str(next.accessType))) because.push('accessType');
    } else because.push(k);
  }
  if (type === 'account' && str(next.status) === ACCOUNT_STATUS.CLOSED && str(prev?.status) !== ACCOUNT_STATUS.CLOSED) because.push('close');
  return { required: because.length > 0, because };
}

// ─── Validación genérica por schema (tipos, requeridos, listas) ──────────────

export type Catalogs = Partial<Record<OmlListId, readonly string[]>>;

const labelOf = (f: OmlFieldDef) => f.label;

function validateField(f: OmlFieldDef, v: unknown, values: Values, catalogs: Catalogs, errors: OmlIssue[]): void {
  const requiredIf = f.requiredIf && sameValue(values[f.requiredIf.field], f.requiredIf.equals);
  const requiredIfPresent = f.requiredIfPresent && !isEmpty(values[f.requiredIfPresent]);
  if ((f.required || requiredIf || requiredIfPresent) && isEmpty(v)) {
    const eq = f.requiredIf?.equals;
    const why = requiredIf
      ? ` (requerido porque ${labelByKey(f.requiredIf!.field)} es "${eq === true ? 'Sí' : eq === false ? 'No' : String(eq)}")`
      : requiredIfPresent ? ` (requerido porque hay ${labelByKey(f.requiredIfPresent!)})` : '';
    errors.push({ field: f.key, code: 'required', message: `Falta ${labelOf(f)}${why}.` });
    return;
  }
  if (isEmpty(v)) return;
  switch (f.type) {
    case 'text':
    case 'richtext':
      if (typeof v !== 'string') errors.push({ field: f.key, code: 'type', message: `${labelOf(f)} tiene que ser texto.` });
      else if (f.key === 'email' && !EMAIL_RE.test(v.trim())) errors.push({ field: f.key, code: 'email', message: `${labelOf(f)} no tiene formato de email válido.` });
      break;
    case 'number':
    case 'percent': {
      if (!isNum(v)) { errors.push({ field: f.key, code: 'type', message: `${labelOf(f)} tiene que ser un número.` }); break; }
      if (f.integer && !Number.isInteger(v)) errors.push({ field: f.key, code: 'integer', message: `${labelOf(f)} tiene que ser un entero.` });
      if (isNum(f.min) && v < f.min) errors.push({ field: f.key, code: 'min', message: `${labelOf(f)} tiene que ser mayor o igual a ${f.min}.` });
      if (isNum(f.max) && v > f.max) errors.push({ field: f.key, code: 'max', message: `${labelOf(f)} tiene que ser menor o igual a ${f.max}.` });
      break;
    }
    case 'date':
      if (!DATE_RE.test(str(v))) errors.push({ field: f.key, code: 'date', message: `${labelOf(f)} tiene que ser una fecha (AAAA-MM-DD).` });
      break;
    case 'dateMonth':
      if (!DATE_MONTH_RE.test(str(v))) errors.push({ field: f.key, code: 'date', message: `${labelOf(f)} tiene que ser un mes (AAAA-MM) o una fecha (AAAA-MM-DD).` });
      break;
    case 'bool':
      if (typeof v !== 'boolean') errors.push({ field: f.key, code: 'type', message: `${labelOf(f)} tiene que ser Sí o No.` });
      break;
    case 'select': {
      const allowed = f.list ? catalogs[f.list] ?? OML_LISTS[f.list].values : [];
      if (typeof v !== 'string' || !allowed.includes(v)) errors.push({ field: f.key, code: 'list', message: `"${String(v)}" no es un valor válido de ${labelOf(f)}.` });
      break;
    }
    case 'multiselect': {
      const allowed = f.list ? catalogs[f.list] ?? OML_LISTS[f.list].values : [];
      const bad = arr(v).filter((x) => !allowed.includes(x));
      if (!Array.isArray(v) || bad.length) errors.push({ field: f.key, code: 'list', message: `${labelOf(f)}: ${bad.map((b) => `"${b}"`).join(', ') || 'valor'} no está en la lista.` });
      break;
    }
    case 'user':
      if (typeof v !== 'string' || !EMAIL_RE.test(v.trim())) errors.push({ field: f.key, code: 'email', message: `${labelOf(f)} tiene que ser un email de Winclap.` });
      break;
    case 'link':
      if (typeof v !== 'string' || !/^https?:\/\/\S+$/i.test(v.trim())) errors.push({ field: f.key, code: 'url', message: `${labelOf(f)} tiene que ser una URL (https://…).` });
      break;
    case 'relations':
      if (!Array.isArray(v)) errors.push({ field: f.key, code: 'type', message: `${labelOf(f)} tiene que ser una lista.` });
      break;
    default:
      break;
  }
}
const OML_SCHEMA_FIELDS_BY_KEY = new Map<string, OmlFieldDef>();
for (const def of Object.values(OML_SCHEMA)) for (const f of def.fields) if (!OML_SCHEMA_FIELDS_BY_KEY.has(f.key)) OML_SCHEMA_FIELDS_BY_KEY.set(f.key, f);
const labelByKey = (key: string): string => OML_SCHEMA_FIELDS_BY_KEY.get(key)?.label ?? key;

/** Únicos dentro de la cuenta (`unique`, opcionalmente con `uniqueWith`), comparados sin tildes ni mayúsculas. */
export function uniqueViolations(type: OmlEntityType, values: Values, others: Values[], selfId?: string): OmlIssue[] {
  const out: OmlIssue[] = [];
  for (const f of OML_SCHEMA[type].fields) {
    if (!f.unique || isEmpty(values[f.key])) continue;
    const mine = foldKey(str(values[f.key]));
    const clash = others.find((o) => o.id !== selfId && !o.archived && foldKey(str(o[f.key])) === mine && (!f.uniqueWith || sameValue(o[f.uniqueWith], values[f.uniqueWith])));
    if (clash) out.push({ field: f.key, code: 'unique', message: `Ya existe ${OML_SCHEMA[type].label.toLowerCase()} con ${f.label.toLowerCase()} "${str(values[f.key])}".`, detail: { id: clash.id } });
  }
  return out;
}

/**
 * Validación de tipos/requeridos/listas de cualquier entidad. Los campos
 * `system` se ignoran (los escribe el backend). Devuelve solo errores.
 */
export function validateBySchema(type: OmlEntityType, values: Values, catalogs: Catalogs = {}): OmlIssue[] {
  const errors: OmlIssue[] = [];
  for (const f of OML_SCHEMA[type].fields) {
    if (f.system) continue;
    validateField(f, values[f.key], values, catalogs, errors);
  }
  return errors;
}

/** Aplica defaults del schema a un registro nuevo. */
export function withDefaults(type: OmlEntityType, values: Values): Values {
  const out: Values = { ...values };
  for (const f of OML_SCHEMA[type].fields) if (f.default !== undefined && out[f.key] === undefined) out[f.key] = f.default;
  return out;
}

// ─── Reglas por entidad (§13.3) ──────────────────────────────────────────────

export interface EntityContext {
  today: string;
  /** Registro anterior (null si es alta). */
  prev: Values | null;
  /** Otros registros del mismo tipo en la cuenta (sin el propio). */
  siblings: Values[];
  /** Marcas de la cuenta (para "requerido si hay más de una marca"). */
  brands?: Values[];
  /** Journeys/campañas/experimentos de la cuenta (segmento en uso, cruces de experimentos). */
  journeys?: Values[];
  campaigns?: Values[];
  experiments?: Values[];
  /** Asignaciones vigentes de la cuenta (cerrar cuenta). */
  assignments?: AssignmentLike[];
  /** Parámetros de URL a eliminar (lista cerrada editable). */
  urlParamsToStrip?: readonly string[];
  /** Confirmación explícita "no es una clave" (Dónde está la credencial). */
  confirmSecretWarning?: boolean;
  /** Justificación escrita del cambio. */
  justification?: string;
  catalogs?: Catalogs;
  leadOnlyProcessTypes?: readonly string[];
  /** Roles del actor en la cuenta (transiciones de estado, bloques lead-only). */
  actorRoles?: readonly OmlActorRole[];
}

const err = (field: string, code: string, message: string, detail?: Record<string, unknown>): OmlIssue => ({ field, code, message, detail });

function brandRequired(values: Values, ctx: EntityContext, errors: OmlIssue[]): void {
  const brands = (ctx.brands ?? []).filter((b) => !b.archived && b.active !== false);
  if (brands.length > 1 && isEmpty(values.brandId)) errors.push(err('brandId', 'brand_required', 'La cuenta tiene más de una marca: hay que indicar a cuál pertenece.'));
}

export function validateEntity(type: OmlEntityType, input: Values, ctx: EntityContext): OmlValidation {
  const values = withDefaults(type, input);
  const errors = validateBySchema(type, values, ctx.catalogs ?? {});
  const warnings: OmlIssue[] = [];
  const computed: Values = {};
  errors.push(...uniqueViolations(type, values, ctx.siblings, str(values.id) || undefined));

  // Transición de estado (cuando la entidad tiene estado y cambia)
  const statusKey = statusFieldOf(type);
  if (statusKey && ctx.prev && !isEmpty(values[statusKey]) && str(ctx.prev[statusKey]) !== str(values[statusKey])) {
    const check = canTransition(type, str(ctx.prev[statusKey]), str(values[statusKey]), ctx.actorRoles ?? []);
    if (!check.ok) errors.push(err(statusKey, check.code === 'role' ? 'status_role' : 'status_transition', check.message!, { allowedRoles: check.allowedRoles }));
  }

  // Justificación
  const jr = justificationRequired(type, ctx.prev, values);
  if (jr.required && isEmpty(ctx.justification)) {
    errors.push(err('justification', 'justification_required', `Este cambio exige una justificación escrita (${jr.because.map(justifyLabel).join(', ')}).`, { because: jr.because }));
  }

  switch (type) {
    case 'account': validateAccount(values, ctx, errors); break;
    case 'stakeholder': break; // requiredIf del schema cubre Tercero ⇒ organización + tipo
    case 'platform': validatePlatform(values, ctx, errors, warnings, computed); break;
    case 'journey': validateJourney(values, ctx, errors, warnings, computed); break;
    case 'campaign': validateCampaign(values, ctx, errors, computed); break;
    case 'experiment': validateExperiment(values, ctx, errors, warnings, computed); break;
    case 'project': validateProject(values, ctx, errors, warnings, computed); break;
    case 'assignment': validateAssignment(values, ctx, errors, computed); break;
    case 'link': validateLink(values, ctx, errors, warnings, computed); break;
    case 'segment': validateSegment(values, ctx, errors); break;
    case 'process':
      if (ctx.actorRoles && !ctx.actorRoles.some((r) => SUPER.includes(r)) && processTypeIsLeadOnly(values.processType, ctx.leadOnlyProcessTypes ?? [])) {
        errors.push(err('processType', 'lead_only', `El bloque "${str(values.processType)}" solo lo edita el Lead OMC.`));
      }
      break;
    default: break;
  }
  return { errors, warnings, computed };
}

const justifyLabel = (k: string) =>
  k === 'close' ? 'cierre de cuenta' : k === 'accessType' ? 'acceso por cuenta compartida o personal' : (OML_SCHEMA_FIELDS_BY_KEY.get(k)?.label ?? k);

function validateAccount(v: Values, ctx: EntityContext, errors: OmlIssue[]): void {
  const start = dateMonthToISO(v.startDate);
  const end = dateMonthToISO(v.endDate);
  if (start && end && end <= start) errors.push(err('endDate', 'end_before_start', 'La fecha de fin prevista tiene que ser posterior a la de inicio.'));
  if (!isEmpty(v.deal) && isEmpty(v.dealValidUntil)) errors.push(err('dealValidUntil', 'deal_needs_validity', 'No se puede completar el Deal actual sin la Fecha de vigencia del deal.'));
  const closing = str(v.status) === ACCOUNT_STATUS.CLOSED && str(ctx.prev?.status) !== ACCOUNT_STATUS.CLOSED;
  if (closing) {
    const active = (ctx.assignments ?? []).filter((a) => isAssignmentActive(a, ctx.today));
    if (active.length) errors.push(err('status', 'active_assignments', `No se puede cerrar la cuenta con ${active.length} asignación(es) de equipo vigente(s): primero hay que cerrarlas.`, { count: active.length }));
  }
}

function validatePlatform(v: Values, ctx: EntityContext, errors: OmlIssue[], warnings: OmlIssue[], computed: Values): void {
  if (v.isCentral === true) {
    const other = ctx.siblings.find((s) => !s.archived && s.id !== v.id && s.isCentral === true && sameValue(s.brandId ?? null, v.brandId ?? null));
    if (other) errors.push(err('isCentral', 'central_exists', `Ya hay una plataforma central para esta cuenta y marca: ${str(other.platform)}${str(other.usage) ? ` (${str(other.usage)})` : ''}.`, { id: other.id, platform: other.platform }));
  }
  const secret = looksLikeSecret(v.credentialLocation);
  if (secret.suspicious) {
    if (!ctx.confirmSecretWarning) {
      errors.push(err('credentialLocation', 'secret_warning', '¿Estás seguro de que esto no es una clave/secreto? Esto podría ser una contraseña: guardá solo la referencia al gestor (por ejemplo "1Password · bóveda OM-Laika") o confirmá explícitamente que es solo una referencia.', { token: secret.token, reason: secret.reason }));
    } else {
      warnings.push(err('credentialLocation', 'secret_confirmed', 'Se guardó con la confirmación explícita de que no es una clave. Queda registrado en el historial.'));
      computed.secretWarningConfirmedAt = ctx.today;
    }
  }
  if (!isEmpty(v.accessUrl)) {
    const s = sanitizeUrl(v.accessUrl, ctx.urlParamsToStrip ?? OML_LISTS.urlParamsToStrip.values);
    if (s.valid) {
      computed.accessUrl = s.url;
      if (s.removed.length) warnings.push(err('accessUrl', 'url_params_removed', `Se quitaron de la URL los parámetros ${s.removed.join(', ')} (transportan credenciales o tokens).`, { removed: s.removed }));
    }
  }
  if (RISKY_ACCESS_TYPES.includes(str(v.accessType))) warnings.push(err('accessType', 'risky_access', `Acceso por ${str(v.accessType).toLowerCase()}: alerta permanente en la ficha y aviso al data owner y al Lead OMC.`));
}

function validateJourney(v: Values, ctx: EntityContext, errors: OmlIssue[], warnings: OmlIssue[], computed: Values): void {
  brandRequired(v, ctx, errors);
  if (str(v.status) === JOURNEY_STATUS.ACTIVE) {
    if (isEmpty(v.activatedAt)) errors.push(err('activatedAt', 'active_needs_date', 'No se puede pasar un journey a Activo sin fecha de activación.'));
    if (isEmpty(v.targetMetric)) errors.push(err('targetMetric', 'active_needs_metric', 'No se puede pasar un journey a Activo sin la métrica que busca mover.'));
    if (arr(v.segmentIds).length === 0) errors.push(err('segmentIds', 'active_needs_segment', 'No se puede pasar un journey a Activo sin al menos un segmento.'));
    if (arr(v.channels).length === 0) errors.push(err('channels', 'active_needs_channel', 'No se puede pasar un journey a Activo sin al menos un canal.'));
  }
  if (str(v.measurementStatus) === MEASUREMENT_STATUS.MEASURED) {
    if (!isNum(v.cvrTest)) errors.push(err('cvrTest', 'measured_needs_cvr', 'No se puede marcar la medición como Medido sin el CVR del grupo test.'));
    if (!isNum(v.cvrControl)) errors.push(err('cvrControl', 'measured_needs_cvr', 'No se puede marcar la medición como Medido sin el CVR del grupo control.'));
  }
  const uplift = computeUplift(v.cvrTest, v.cvrControl);
  const incremental = computeIncremental(v.nTest, v.cvrTest, v.cvrControl);
  computed.uplift = uplift;
  computed.incrementalConversions = incremental;
  if (uplift !== null && incremental === null) warnings.push(err('nTest', 'incremental_pending', 'Sin la audiencia alcanzada (N test) no se pueden calcular las conversiones incrementales: queda como dato pendiente.'));
}

function validateCampaign(v: Values, ctx: EntityContext, errors: OmlIssue[], computed: Values): void {
  brandRequired(v, ctx, errors);
  const hasResults = ['sends', 'influencedConversions', 'cvrCommunication', 'cvrControl'].some((k) => !isEmpty(v[k]));
  if (hasResults && isEmpty(v.measuredPeriod)) errors.push(err('measuredPeriod', 'results_need_period', 'Hay resultados cargados: falta el período medido.'));
  computed.uplift = computeUplift(v.cvrCommunication, v.cvrControl);
  computed.incrementalConversions = computeIncremental(v.sends, v.cvrCommunication, v.cvrControl);
}

/** "Una sola variable": sin comas, punto y coma ni " y " separando dos cosas (§5.2, AC-30). */
export function isSingleVariable(text: unknown): boolean {
  const s = str(text).trim();
  if (!s) return true;
  if (/[,;]/.test(s)) return false;
  return !/\s+y\s+/i.test(s);
}

/** Cruce anti-contaminación: mismo segmento, mismo canal y misma métrica primaria (§13.3, AC-29). */
export function experimentConflicts(candidate: Values, running: Values[]): Values[] {
  const segs = new Set(arr(candidate.segmentIds));
  const chans = new Set(arr(candidate.channels).map(foldKey));
  const metric = foldKey(str(candidate.primaryMetric));
  return running.filter((e) =>
    e.id !== candidate.id && !e.archived && str(e.status) === EXPERIMENT_STATUS.RUNNING
    && arr(e.segmentIds).some((s) => segs.has(s))
    && arr(e.channels).some((c) => chans.has(foldKey(c)))
    && foldKey(str(e.primaryMetric)) === metric,
  );
}

const RUNNING_REQUIRED: [string, string][] = [
  ['hypothesis', 'hipótesis'], ['variable', 'variable que se prueba'], ['primaryMetric', 'métrica primaria'], ['baseline', 'baseline'], ['mde', 'MDE'],
  ['sampleSize', 'tamaño de muestra'], ['splitTest', 'split test'], ['noImprovementPlan', 'plan si no hay mejora'],
];

function validateExperiment(v: Values, ctx: EntityContext, errors: OmlIssue[], warnings: OmlIssue[], computed: Values): void {
  if (!isEmpty(v.journeyId) && !isEmpty(v.campaignId)) errors.push(err('campaignId', 'journey_xor_campaign', 'Un experimento se asocia a un Journey o a una Campaña, no a los dos.'));
  if (!isSingleVariable(v.variable)) errors.push(err('variable', 'single_variable', 'La variable que se prueba tiene que ser una sola: dividí el experimento en dos.'));
  const complex = str(v.flowComplexity) === 'Complejo';
  const path = str(v.implementationPath);
  if (path && ((complex && path !== 'Pausar el flujo viejo y activar el nuevo') || (!complex && path !== 'Correr las dos versiones en simultáneo'))) {
    warnings.push(err('implementationPath', 'path_mismatch', complex ? 'Para un flujo complejo se recomienda pausar el flujo viejo y activar el nuevo.' : 'Para un flujo simple se recomienda correr las dos versiones en simultáneo.'));
  }
  const status = str(v.status);
  const prevStatus = str(ctx.prev?.status);
  if (status === EXPERIMENT_STATUS.RUNNING) {
    for (const [k, label] of RUNNING_REQUIRED) if (isEmpty(v[k])) errors.push(err(k, 'running_requires', `No se puede poner el experimento en Corriendo sin ${label}.`));
    if (v.hasControlGroup === true && isEmpty(v.controlGroupLevel)) errors.push(err('controlGroupLevel', 'running_requires', 'No se puede poner el experimento en Corriendo sin definir el nivel del grupo control.'));
    if (v.hasControlGroup === false && isEmpty(v.noControlReason)) errors.push(err('noControlReason', 'running_requires', 'No se puede poner el experimento en Corriendo sin explicar por qué no hay grupo control.'));
    if (prevStatus !== EXPERIMENT_STATUS.RUNNING) {
      const conflicts = experimentConflicts(v, ctx.experiments ?? []);
      if (conflicts.length) errors.push(err('status', 'experiment_conflict', `Ya hay un experimento Corriendo en la cuenta con el mismo segmento, canal y métrica primaria: ${conflicts.map((c) => `"${str(c.name)}"`).join(', ')}.`, { conflicts: conflicts.map((c) => ({ id: c.id, name: c.name })) }));
    }
  }
  if (prevStatus === EXPERIMENT_STATUS.RUNNING) {
    if (!sameValue(ctx.prev?.primaryMetric, v.primaryMetric)) errors.push(err('primaryMetric', 'metric_locked', 'No se puede cambiar la métrica primaria de un experimento que ya está Corriendo.'));
    const prevEnd = str(ctx.prev?.plannedEndDate);
    if (prevEnd && str(v.plannedEndDate) && str(v.plannedEndDate) < prevEnd) errors.push(err('plannedEndDate', 'end_date_locked', 'No se puede adelantar la fecha de cierre planificada de un experimento Corriendo.'));
  }
  if (status === EXPERIMENT_STATUS.CLOSED) {
    if (isEmpty(v.result)) errors.push(err('result', 'close_requires', 'No se puede cerrar un experimento sin resultado.'));
    if (typeof v.reachedSignificance !== 'boolean') errors.push(err('reachedSignificance', 'close_requires', 'No se puede cerrar un experimento sin indicar si alcanzó significancia.'));
    if (isEmpty(v.decision)) errors.push(err('decision', 'close_requires', 'No se puede cerrar un experimento sin decisión.'));
    if (isEmpty(v.learning)) errors.push(err('learning', 'close_requires', 'No se puede cerrar un experimento sin aprendizaje.'));
    if (v.reachedSignificance === false && isEmpty(v.noEffectDiagnosis)) errors.push(err('noEffectDiagnosis', 'close_requires', 'No alcanzó significancia: falta el diagnóstico.'));
  }
  if (isNum(v.splitTest)) computed.splitControl = 100 - (v.splitTest as number);
  computed.nonCausal = v.randomAssignment === false;
  computed.possiblyBiased = v.crossesAtypicalDate === true;
}

function validateProject(v: Values, ctx: EntityContext, errors: OmlIssue[], warnings: OmlIssue[], computed: Values): void {
  const status = str(v.status);
  if (v.briefComplete !== true && !isEmpty(v.committedDeadline)) {
    errors.push(err('committedDeadline', 'deadline_needs_brief', 'No se puede cargar un deadline comprometido con la bajada incompleta. Se puede producir; no se puede prometer.'));
  }
  if (v.assetsReceived === true) computed.productionClockStart = str(v.assetsReceivedAt) || null;
  else computed.productionClockStart = null;
  if (str(v.assetsRequestedAt) && str(v.briefReceivedAt) && str(v.assetsRequestedAt) > str(v.briefReceivedAt)) {
    computed.assetsRequestDeviation = true;
    warnings.push(err('assetsRequestedAt', 'assets_request_deviation', 'Desvío: los assets se pidieron después de la bajada del brief (se piden el día del intake).'));
  } else computed.assetsRequestDeviation = false;
  const PRODUCTION_OR_LATER: readonly string[] = [PROJECT_STATUS.PRODUCTION, PROJECT_STATUS.INTERNAL_REVIEW, PROJECT_STATUS.CLIENT_VALIDATION, PROJECT_STATUS.APPROVED, PROJECT_STATUS.DELIVERED];
  if (PRODUCTION_OR_LATER.includes(status) && v.assetsReceived !== true) errors.push(err('status', 'production_needs_assets', 'No se puede pasar el proyecto a En producción sin assets recibidos. El brief admite supuestos, los assets no.'));
  if ((status === PROJECT_STATUS.APPROVED || status === PROJECT_STATUS.DELIVERED) && v.qaDone !== true) errors.push(err('qaDone', 'approved_needs_qa', 'No se puede pasar el proyecto a Aprobado sin QA hecho.'));
  // Al guardar Assets recibidos en No sobre una bajada recibida, el sistema lo deja Esperando assets (§13.2).
  if (status === PROJECT_STATUS.BRIEF && v.assetsReceived !== true) computed.status = PROJECT_STATUS.AWAITING_ASSETS;
  // Si la recepción de assets se atrasa, se propone correr el deadline la misma cantidad de días hábiles.
  const prevReceived = str(ctx.prev?.assetsReceivedAt);
  const nowReceived = str(v.assetsReceivedAt);
  const deadline = str(v.committedDeadline);
  if (deadline && prevReceived && nowReceived && nowReceived > prevReceived) {
    const delay = businessDaysBetweenISO(prevReceived, nowReceived);
    if (delay > 0) {
      computed.suggestedDeadline = addBusinessDaysISO(deadline, delay);
      warnings.push(err('committedDeadline', 'deadline_suggestion', `Los assets llegaron ${delay} día(s) hábil(es) después: se propone correr el deadline a ${computed.suggestedDeadline}.`, { delay, suggestedDeadline: computed.suggestedDeadline }));
    }
  }
  // Deadline sobre bajada que después se marca incompleta (§15): aviso, no bloqueo.
  if (ctx.prev && ctx.prev.briefComplete === true && v.briefComplete === false && !isEmpty(ctx.prev.committedDeadline)) {
    warnings.push(err('briefComplete', 'brief_reopened_with_deadline', 'Este proyecto tenía un deadline comprometido y la bajada volvió a marcarse como incompleta.'));
    computed.briefReopenedWithDeadline = true;
  }
}

function validateAssignment(v: Values, ctx: EntityContext, errors: OmlIssue[], computed: Values): void {
  if (str(v.personType) === PERSON_TYPE.WINCLAP) {
    computed.email = normalizeEmail(str(v.email));
    computed.externalName = null;
  } else {
    computed.email = null;
  }
  if (str(v.from) && str(v.to) && str(v.to) < str(v.from)) errors.push(err('to', 'to_before_from', 'La fecha Hasta no puede ser anterior a Desde.'));
  if (v.isPrimary === true) {
    const other = ctx.siblings.find((s) => s.id !== v.id && !s.archived && s.isPrimary === true && str(s.role) === str(v.role) && isAssignmentActive(s as AssignmentLike, ctx.today));
    if (other) errors.push(err('isPrimary', 'primary_exists', `Ya hay un responsable principal con rol ${str(v.role)}: ${str(other.displayName) || str(other.email) || str(other.externalName)}.`, { id: other.id }));
  }
}

function validateLink(v: Values, ctx: EntityContext, errors: OmlIssue[], warnings: OmlIssue[], computed: Values): void {
  if (!isEmpty(v.url)) {
    const s = sanitizeUrl(v.url, ctx.urlParamsToStrip ?? OML_LISTS.urlParamsToStrip.values);
    if (s.valid) {
      computed.url = s.url;
      if (s.removed.length) warnings.push(err('url', 'url_params_removed', `Se quitaron de la URL los parámetros ${s.removed.join(', ')} (transportan credenciales o tokens).`, { removed: s.removed }));
    }
  }
  void errors;
}

/** Un segmento en uso por un journey activo, una campaña activa o un experimento corriendo no se puede dar de baja (§13.3, AC-25). */
export function segmentUsers(segmentId: string, ctx: Pick<EntityContext, 'journeys' | 'campaigns' | 'experiments'>): { type: 'journey' | 'campaign' | 'experiment'; id: string; name: string }[] {
  const uses = (list: Values[] | undefined, type: 'journey' | 'campaign' | 'experiment', active: (x: Values) => boolean) =>
    (list ?? []).filter((x) => !x.archived && active(x) && arr(x.segmentIds).includes(segmentId)).map((x) => ({ type, id: str(x.id), name: str(x.name) }));
  return [
    ...uses(ctx.journeys, 'journey', (j) => str(j.status) === JOURNEY_STATUS.ACTIVE),
    ...uses(ctx.campaigns, 'campaign', (c) => c.active !== false),
    ...uses(ctx.experiments, 'experiment', (e) => str(e.status) === EXPERIMENT_STATUS.RUNNING),
  ];
}

function validateSegment(v: Values, ctx: EntityContext, errors: OmlIssue[]): void {
  if (v.active === false && ctx.prev && ctx.prev.active !== false && str(v.id)) {
    const users = segmentUsers(str(v.id), ctx);
    if (users.length) errors.push(err('active', 'segment_in_use', `No se puede marcar el segmento como inactivo: lo usan ${users.map((u) => `${u.type === 'journey' ? 'el journey' : u.type === 'campaign' ? 'la campaña' : 'el experimento'} "${u.name}"`).join(', ')}.`, { users }));
  }
}

// ─── Restringidos (§9.3) ─────────────────────────────────────────────────────

export interface RestrictedAccess {
  canSeeCommercial: boolean;
  canSeeCredential: boolean;
}

/** Quita del registro los campos restringidos que el usuario no puede ver y devuelve cuáles quedaron "con candado". */
export function maskRestricted<T extends Values>(type: OmlEntityType, values: T, access: RestrictedAccess): { values: T; locked: string[] } {
  const locked: string[] = [];
  const out: Values = { ...values };
  for (const f of OML_SCHEMA[type].fields) {
    if (!f.restricted) continue;
    const can = f.restricted === 'credential' ? access.canSeeCredential : access.canSeeCommercial;
    if (!can) {
      delete out[f.key];
      locked.push(f.key);
    }
  }
  return { values: out as T, locked };
}

export function restrictedGroupOf(type: OmlEntityType, key: string): OmlRestrictedGroup | null {
  return OML_SCHEMA[type].fields.find((f) => f.key === key)?.restricted ?? null;
}

/** El campo "Dónde está la credencial" no se exporta nunca, en ningún formato ni para ningún rol (§12.5, AC-17). */
export function isNeverExported(type: OmlEntityType, key: string): boolean {
  return restrictedGroupOf(type, key) === 'credential';
}

// ─── Solicitudes de acceso y plazos (§8.4, §10.2, §13.1, §15) ────────────────

/** Ajena al área = su área/team del community map no está entre las "propias" (Owned Media, Retention, Growth, Dirección). */
export function isExternalRequester(member: { area?: string | null; team?: string | null } | null, ownAreas: readonly string[]): boolean {
  if (!member) return true;
  const own = ownAreas.map(foldKey);
  return !own.includes(foldKey(member.area ?? '')) && !own.includes(foldKey(member.team ?? ''));
}

/** Una solicitud sin respuesta escala al Lead OMC a los N días hábiles (5 por defecto). Nunca se aprueba sola. */
export function escalationDue(createdAtIso: string, businessDays: number, today: string): boolean {
  return addBusinessDaysISO(createdAtIso.slice(0, 10), businessDays) <= today;
}

/** Un proyecto lleva N días hábiles esperando assets (§15). */
export function awaitingAssetsFor(project: Values, today: string): number | null {
  if (str(project.status) !== PROJECT_STATUS.AWAITING_ASSETS) return null;
  const since = str(project.awaitingAssetsSince || project.updatedAt).slice(0, 10);
  return since ? businessDaysBetweenISO(since, today) : null;
}

/** Vencimientos de acceso: 30, 15 y 7 días antes (§15). Devuelve el hito que cae HOY, si alguno. */
export function accessExpiryMilestone(expiresAt: unknown, today: string, milestones: readonly number[]): number | null {
  const d = str(expiresAt).slice(0, 10);
  if (!d) return null;
  const left = daysBetweenISO(today, d);
  return milestones.includes(left) ? left : null;
}

export function accessExpiringSoon(expiresAt: unknown, today: string, days = 30): boolean {
  const d = str(expiresAt).slice(0, 10);
  return !!d && daysBetweenISO(today, d) <= days;
}

/** Freelo asignable a un proyecto = asignación de la cuenta con rol de diseño o maquetación. */
export function isDesignAssignment(a: AssignmentLike): boolean {
  return DESIGN_ROLES.includes(str(a.role));
}

/** Acceso riesgoso (alerta permanente en la ficha). */
export function isRiskyAccess(accessType: unknown): boolean {
  return RISKY_ACCESS_TYPES.includes(str(accessType)) && str(accessType) !== ACCESS_TYPE.SSO;
}
